import type {
  IFooterLinkChildren,
  ILegalLink,
  IPaymentLink,
  ISocialItem,
  IFooterLink,
  IFooterAward
} from '@/components/main-footer/types'
import getLinkRel from '@/utils/getLinkRel'
import type {
  IGlobalAlert,
  ILogo,
  ICompactImage,
  INavigation,
  IProfile,
  ISection,
  ISectionChildItem,
  ISectionItem,
  ITabs,
  ITopBarItem
} from '@/components/main-header/types'
import type { IBreadcrumb, ITranslatedSlug } from '@/components/types'
import type {
  BlogStoryblok,
  LinkStoryblok,
  MainNavigationStoryblok,
  NavigationSectionStoryblok,
  GlobalAlertStoryblok,
  AssetStoryblok
} from '@/storyblok/component-types-sb'
import type { TIcons } from '@/components/icon/types'
import type { IStoryblokPageData } from '@/stores/types'

export const parseSocials = (sbSocials: any) => {
  if (!sbSocials) {
    return []
  }

  const socials: ISocialItem[] = []
  sbSocials.forEach((item: any) => {
    const socialItemLink = item?.content?.link
    const socialItemIcon = item?.content?.icon

    if (!socialItemLink || !socialItemIcon) {
      return []
    }

    socials.push({
      link: socialItemLink.url,
      target: socialItemLink.target,
      rel: getLinkRel(socialItemLink.target),
      name: item.name
    })
  })

  return socials
}

export const parseContactUs = (sbContactUs: any) => {
  if (!sbContactUs) {
    return null
  }

  return {
    href: sbContactUs.href.url,
    target: '_self',
    text: sbContactUs.text
  }
}

export const parseLegal = (sbLegal: any) => {
  if (!sbLegal) {
    return []
  }

  const legalLinks: ILegalLink[] = []
  sbLegal.forEach((link: any) => {
    const linkContent = link?.content

    if (!linkContent) {
      return []
    }

    if (linkContent?.whitelistCountries.length > 0) {
      /* TOOD: check if country is in SB whitelist country */
      const found = linkContent?.whitelistCountries.filter((item: any) => item === 'LV')

      return found.length === 0
    }

    legalLinks.push({
      _uid: link._uid,
      href: linkContent?.href?.url,
      text: linkContent.text
    })
  })

  return legalLinks
}

export const parseCopyright = (sbCopyright: any) => {
  if (!sbCopyright) {
    return null
  }

  return {
    href: sbCopyright.href.url,
    target: '_self',
    text: sbCopyright.text
  }
}

export const parsePaymentLogos = (sbPayments: any) => {
  if (!sbPayments) {
    return []
  }

  const paymentLogos: IPaymentLink[] = []
  sbPayments.forEach((item: any) => {
    const linkContent = item?.content?.link?.content
    if (!linkContent) {
      return null
    }

    paymentLogos.push({
      title: linkContent.text,
      href: linkContent.href.url,
      name: item.name.toLowerCase()
    })
  })

  return paymentLogos
}

export const parseNavigationLinks = (sectionContent: any) => {
  if (!sectionContent) {
    return []
  }

  const footerLinks: IFooterLink[] = []

  sectionContent?.items.forEach((section: any) => {
    const sectionContent = section?.title?.content

    if (!sectionContent) {
      return
    }

    let sectionTitle = sectionContent?.text
    let sectionHref = sectionContent?.href?.url

    if (section?.customTitle) {
      sectionTitle = section.customTitle
      sectionHref = null
    }

    const children: IFooterLinkChildren[] = []

    section.items.forEach((item: any) => {
      const content = item?.link?.content

      if (!content) {
        return
      }

      children.push({
        label: content.text,
        href: content.href.url,
        linkTarget: content.href.target,
        rel: getLinkRel(content.href.target),
        key: item.link.slug,
        external: content.href.target === '_blank'
      })
    })

    footerLinks.push({
      label: sectionTitle,
      href: sectionHref,
      key: section._uid,
      external: sectionContent?.href?.target === '_blank',
      children
    })
  })

  return footerLinks
}

export const parseSemantics = (storyContent: any) => {
  return {
    searchPageUrl: storyContent?.searchPageUrl?.content?.href?.url,
    logo: storyContent.logo.filename,
    email: storyContent.email,
    address: storyContent.address,
    phone: storyContent.phone
  }
}

export const parseFooterAwards = (content: any[]): IFooterAward[] => {
  return content.map((award) => {
    return {
      title: award.title,
      src: award.image.filename,
      alt: award.image.alt,
      _uid: award._uid
    }
  })
}

export const parseTabs = (sbContent: MainNavigationStoryblok) => {
  if (!sbContent?.tabs) {
    return []
  }

  const tabs: ITabs[] = []
  sbContent.tabs.forEach((tab) => {
    if (typeof tab === 'object' && 'content' in tab) {
      tabs.push({
        label: tab.content.text,
        key: tab.name,
        image: parseImageObject(tab.content.image),
        icon: String(tab.content.icon) as TIcons
      })
    }
  })

  return tabs
}

export const parseStoryContent = (sbContent: MainNavigationStoryblok) => {
  if (!sbContent?.body) {
    return []
  }

  const sections: ISection[] = []
  const sbContentBody = sbContent.body as NavigationSectionStoryblok[]
  sbContentBody.forEach((section) => {
    let label = typeof section?.title === 'object' ? section?.title?.content?.text : ''
    const items: ISectionItem[] = []

    if (section?.customTitle) {
      label = section.customTitle
    }

    if (section?.items?.length) {
      section.items
        .filter((item) => typeof item.link === 'object' && 'id' in item.link && item.link?.id)
        .forEach((item) => {
          const children: ISectionChildItem[] = []
          if (item.children) {
            item.children.forEach((child) => {
              if (typeof child === 'object' && 'content' in child) {
                children.push({
                  label: child.content.text,
                  href: child.content.href.cached_url || child.content.href.url,
                  image: parseImageObject(child.content.image),
                  isCard: !!child.content.isCard
                })
              }
            })
          }

          if (typeof item.link === 'object' && 'content' in item.link) {
            items.push({
              label: item.link.content.text,
              href: item.link.content.href.cached_url || item.link.content.href.url,
              items: children,
              isCard: !!item.link.content.isCard,
              image: parseImageObject(item.link.content.image),
              description: item.link.content.description ?? ''
            })
          }
        })
    }

    const hasBlogBlock = hasBlogComponent(section?.blocks),
      gridCols = items?.every((item) => item.isCard) || hasBlogBlock ? 6 : 5

    if (items.length) {
      sections.push({
        label,
        items,
        hasBlogBlock,
        gridCols
      })
    }
  })

  return sections
}

const parseImageObject = (image: AssetStoryblok | undefined): ICompactImage => {
  if (typeof image === 'object' && 'filename' in image) {
    return {
      src: image.filename,
      alt: image.alt ?? ''
    }
  }

  return {
    alt: '',
    src: ''
  }
}

const hasBlogComponent = (blocks?: { content: BlogStoryblok }) => {
  return blocks?.content?.component === 'blog'
}

export const parseBreadcrumbs = (page: Ref<IStoryblokPageData | undefined>, slugName: string, locale: string) => {
  const items: IBreadcrumb[] = []

  const breadcrumbs = page.value?.content?.breadcrumbs
  const pageName = page.value?.name ?? ''

  if (!breadcrumbs || typeof breadcrumbs === 'string') {
    return items
  }

  breadcrumbs.content?.links.forEach((link: any) => {
    if (!link?.content?.text) return

    items.push({
      name: link.content.text,
      href: link.content.href.url
    })
  })

  items.push({
    name: locale === 'en' ? pageName : slugName,
    href: '/' + page.value?.full_slug
  })

  return items
}

export const parseTranslatedSlugs = (translatedSlugs: any, locale: string) => {
  const slugs = translatedSlugs.filter((slug: ITranslatedSlug) => slug.lang === locale)

  return slugs.length ? slugs[0] : ''
}

const parseProfileLinks = (links: any[]) => {
  return links.map((link: any) => ({
    current: false,
    label: link.content.text,
    href: link.content.href.url
  }))
}

export const getLogoData = (topBarContent: any): ILogo | null => {
  if (!topBarContent) {
    return null
  }

  return {
    background: topBarContent.logoBg,
    icon: {
      alt: topBarContent.logo.alt,
      source: topBarContent.logo.filename
    }
  }
}

export const getProfileData = (profileContent: any): IProfile | null => {
  if (!profileContent) {
    return null
  }

  return {
    title: profileContent.title,
    greeting: profileContent.greeting,
    newProfileLinks: parseProfileLinks(profileContent.newProfileLinks),
    oldProfileLinks: parseProfileLinks(profileContent.oldProfileLinks),
    disclaimer: profileContent.body
  }
}

export const getNavigationData = (buttons: any) => {
  if (!buttons) {
    return null
  }

  const navigation: INavigation[] = []

  buttons.forEach((buttonSection: any) => {
    const buttonContent = buttonSection?.content

    const dataForNavigation: ITopBarItem = {
      component: buttonContent.component,
      title: buttonContent.title
    }

    switch (buttonContent?.component) {
      case 'header-search':
        Object.assign(dataForNavigation, {
          link: buttonContent.searchLink.content.href.url,
          placeholder: buttonContent.placeholder
        })
        break
      case 'header-link':
        Object.assign(dataForNavigation, {
          link: buttonContent.link.content.href.url
        })
        break
      case 'header-offers':
        Object.assign(dataForNavigation, {
          total: buttonContent.offers?.length ?? 0,
          offers: []
        })
        break
    }

    navigation.push(dataForNavigation)
  })

  return navigation
}

export const parseGlobalAlert = (disclaimer: GlobalAlertStoryblok) => {
  const content = disclaimer?.content

  const data: IGlobalAlert = {
    isBold: !!content?.bold,
    closable: !!content?.closable,
    showDefaultIcon: !!content?.showDefaultIcon,
    type: content?.type || 'info',
    content: content?.content,
    icon: null
  }

  if (!disclaimer.showDefaultIcon && content?.icon) {
    data.icon = {
      alt: content?.icon.alt ?? '',
      source: content?.icon.filename
    }
  }

  return data
}

export const getCompactLinkData = (link: LinkStoryblok) => ({
  href: (link?.href.url ?? '') as string,
  text: link?.text ?? ''
})

export const transformSBUrl = (url?: string) => {
  if (!url) return ''

  const config = useRuntimeConfig()

  return url.replace(config.public.sbAssetUrl, config.public.assets)
}
